import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  serverTimestamp,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FirestoreUser } from './creditService';
import { User } from 'firebase/auth';

/**
 * Get all users for a company
 */
export const getUsersByCompany = async (companyId: string): Promise<FirestoreUser[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FirestoreUser[];
  } catch (error) {
    console.error('Error getting users by company:', error);
    return [];
  }
};

/**
 * Update user role
 */
export const updateUserRole = async (userId: string, role: 'user' | 'admin' | 'super_admin'): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Update user status
 */
export const updateUserStatus = async (userId: string, status: 'active' | 'inactive'): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

/**
 * Create or update user and company for SSO authentication
 */
export const ensureUserAndCompanyExist = async (user: User): Promise<void> => {
  if (!user.email) {
    throw new Error('User email is required for SSO');
  }

  const domain = getCompanyDomainFromEmail(user.email);
  const companyId = generateCompanyId(domain);
  
  // Check if user already exists
  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    // Create new user
    const userData: Partial<FirestoreUser> = {
      id: user.uid,
      email: user.email,
      name: user.displayName || user.email.split('@')[0],
      companyId,
      role: 'user', // Default role for SSO users
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(userRef, userData);
  }
  
  // Check if company exists
  const companyRef = doc(db, 'companies', companyId);
  const companyDoc = await getDoc(companyRef);
  
  if (!companyDoc.exists()) {
    // Create new company
    const companyData = {
      id: companyId,
      name: domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1),
      domain,
      creditBalance: 0, // Starting credits
      subscriptionPlan: 'free',
      subscriptionStatus: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(companyRef, companyData);
  }
};

/**
 * Extract company domain from email
 */
export const getCompanyDomainFromEmail = (email: string): string => {
  return email.split('@')[1];
};

/**
 * Generate company ID from domain (e.g., company.com -> company_com)
 */
export const generateCompanyId = (domain: string): string => {
  return domain.replace(/\./g, '_').toLowerCase();
};